import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Lock, X, DollarSign, Tag } from "lucide-react";
import { toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, ExpressCheckoutElement, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { API } from "@/lib/api";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CheckoutForm = ({ tier, tierInfo, termsAccepted }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [expressReady, setExpressReady] = useState(false);

  const confirmPayment = async () => {
    if (!termsAccepted) {
      setError("Please agree to the terms and conditions before subscribing.");
      return;
    }
    if (!stripe || !elements) return;
    setProcessing(true);
    setError(null);
    const { error: submitError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/subscription/success?new_user=true`,
      },
    });
    if (submitError) {
      setError(submitError.message);
      setProcessing(false);
    }
  };

  return (
    <div data-testid="payment-element-form">
      <div className="mb-5 pb-4 border-b border-white/10">
        <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Order Summary</p>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white font-semibold">Paladin {tierInfo.name}</p>
            <p className="text-gray-500 text-xs">Recurring {tierInfo.interval}ly subscription</p>
          </div>
          <div className="text-right">
            <p className="text-white text-lg font-bold">{tierInfo.priceDisplay}</p>
            <p className="text-gray-500 text-xs">/{tierInfo.interval}</p>
          </div>
        </div>
      </div>
      <div data-testid="express-checkout-container">
        <ExpressCheckoutElement
          onConfirm={() => confirmPayment()}
          onReady={(e) => { if (e.availablePaymentMethods) setExpressReady(true); }}
          options={{
            layout: { maxColumns: 4, maxRows: 2, overflow: "auto" },
            wallets: { applePay: "always", googlePay: "always" },
            buttonType: { applePay: "subscribe", googlePay: "subscribe" },
          }}
        />
      </div>
      {expressReady && (
        <div className="flex items-center gap-4 my-4">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-gray-500 text-xs">or pay with card</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>
      )}
      <form onSubmit={(e) => { e.preventDefault(); confirmPayment(); }}>
        <PaymentElement options={{
          layout: "tabs",
          defaultValues: { billingDetails: {} },
          paymentMethodOrder: ["card", "apple_pay", "google_pay", "link", "amazon_pay", "cashapp", "klarna", "us_bank_account"],
        }} />
        {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
        <button type="submit" disabled={!stripe || processing || !termsAccepted}
          className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:text-gray-400 text-white font-bold py-4 rounded-xl mt-6 transition-colors text-base"
          data-testid="pay-btn">
          {processing ? "Processing..." : "Subscribe"}
        </button>
      </form>
    </div>
  );
};

const CustomCheckout = ({ tier, tierInfo, couponCode, referralSlug, email, termsAccepted }) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initCheckout = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API}/payments/create-subscription`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            tier, email: email.trim(),
            coupon_code: couponCode || "",
            referral_slug: referralSlug || "",
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Failed to start checkout");
        setClientSecret(data.client_secret);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    if (email) initCheckout();
  }, [tier, email, couponCode, referralSlug]);

  if (loading) return (
    <div className="flex items-center justify-center py-8">
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
      <span className="ml-3 text-gray-400">Loading payment form...</span>
    </div>
  );
  if (error) return (
    <div className="text-center py-6">
      <p className="text-red-400 mb-4">{error}</p>
      <button onClick={() => window.location.reload()} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors">Try Again</button>
    </div>
  );
  if (!clientSecret) return null;

  return (
    <Elements stripe={stripePromise} options={{
      clientSecret,
      appearance: {
        theme: "night",
        variables: { colorPrimary: "#3B82F6", colorBackground: "#0f172a", colorText: "#ffffff", colorDanger: "#ef4444", fontFamily: "system-ui, sans-serif", borderRadius: "10px", spacingUnit: "4px" },
        rules: {
          ".Tab": { backgroundColor: "#0f172a", border: "1px solid #1E293B", color: "#94a3b8" },
          ".Tab:hover": { backgroundColor: "#1E293B", color: "#ffffff" },
          ".Tab--selected": { backgroundColor: "#1E293B", borderColor: "#3B82F6", color: "#ffffff" },
          ".Input": { backgroundColor: "#141B2D", borderColor: "#1E293B", color: "#ffffff" },
          ".Input:focus": { borderColor: "#3B82F6", boxShadow: "0 0 0 1px rgba(59, 130, 246, 0.3)" },
          ".Label": { color: "#94a3b8" },
        },
      },
    }}>
      <CheckoutForm tier={tier} tierInfo={tierInfo} termsAccepted={termsAccepted} />
    </Elements>
  );
};

export default function CheckoutModal({ open, onClose, tier, tierInfo, couponCode: initialCode = "", referralSlug = "" }) {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [promoCode, setPromoCode] = useState(initialCode);
  const [showPromo, setShowPromo] = useState(!!initialCode);

  // Auto-apply code from URL params (?code=START33 or ?promo=START33)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlCode = params.get("code") || params.get("promo") || params.get("coupon");
    if (urlCode) {
      setPromoCode(urlCode.toUpperCase());
      setShowPromo(true);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync if parent passes a new couponCode
  useEffect(() => {
    if (initialCode) {
      setPromoCode(initialCode);
      setShowPromo(true);
    }
  }, [initialCode]);

  if (!open || !tierInfo) return null;

  const emailValid = email.trim() && email.includes("@");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="w-[90%] max-w-2xl bg-[#0A0F1A] border border-white/10 rounded-2xl max-h-[90vh] overflow-y-auto my-8">
        <div className="sticky top-0 bg-[#0A0F1A] border-b border-white/10 p-6 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">Complete Your Subscription</h2>
              <p className="text-gray-400 text-sm mt-1">
                {tierInfo.name} — {tierInfo.priceDisplay}/{tierInfo.interval}
                <span className="ml-2 text-green-400 text-xs">Cancel anytime</span>
              </p>
            </div>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors" data-testid="close-checkout">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="p-6">
          <div className="mb-5">
            <label className="block text-sm text-gray-400 mb-1.5">Email Address</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com" autoFocus
              className="w-full bg-[#141B2D] border border-[#1E293B] rounded-xl px-4 py-3 text-white text-sm focus:border-blue-500 outline-none transition-all placeholder:text-slate-600"
              data-testid="checkout-email-input" />
          </div>
          {/* Promo Code — subtle expandable */}
          <div className="mb-5">
            {!showPromo ? (
              <button onClick={() => setShowPromo(true)} className="text-xs text-white/30 hover:text-white/60 transition-colors" data-testid="show-promo">
                Have a promo code?
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <Tag className="w-4 h-4 text-white/30 flex-shrink-0" />
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="Enter code"
                  className="flex-1 bg-[#141B2D] border border-[#1E293B] rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 outline-none placeholder:text-slate-600"
                  data-testid="promo-input"
                />
                {promoCode && (
                  <span className="text-green-400 text-xs font-medium">Applied</span>
                )}
              </div>
            )}
          </div>
          {/* Payment — always visible once email is entered */}
          {emailValid ? (
            <div className="bg-[#0B0F1A] border border-white/10 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-2"><DollarSign className="w-5 h-5 text-green-400" /><span className="font-semibold text-white">Payment</span></div>
                <div className="flex items-center gap-2 text-xs text-gray-400"><Lock className="w-4 h-4" /><span>Secured by Stripe</span></div>
              </div>
              <div className="p-4">
                <CustomCheckout tier={tier} tierInfo={tierInfo} couponCode={promoCode} referralSlug={referralSlug} email={email} termsAccepted={consent} />
              </div>
              {/* Terms — right above Subscribe button */}
              <div className="px-4 pb-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                    className="w-4 h-4 mt-0.5 rounded border-[#2D3B4F] bg-[#1E293B] text-blue-500 focus:ring-blue-500" data-testid="terms-checkbox" />
                  <p className="text-gray-400 text-xs leading-relaxed">
                    I confirm I am 18+ and agree to the{" "}
                    <a href="/legal/terms" target="_blank" className="text-blue-400 hover:underline">Terms</a>,{" "}
                    <a href="/legal/privacy" target="_blank" className="text-blue-400 hover:underline">Privacy Policy</a>,{" "}
                    <a href="/legal/cancellation" target="_blank" className="text-blue-400 hover:underline">Cancellation Policy</a>, and{" "}
                    <a href="/legal/waiver" target="_blank" className="text-blue-400 hover:underline">Health Waiver</a>.
                    I authorize recurring billing until cancelled.
                  </p>
                </label>
              </div>
            </div>
          ) : (
            <div className="bg-[#0B0F1A] border border-white/10 rounded-xl p-6 text-center">
              <Lock className="w-8 h-8 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">Enter your email to see payment options</p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
